<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

// "⋮" of the companies header: sort field and direction, same menu as the
// contacts header so both screens read the same.
const props = defineProps({
  activeSort: { type: String, default: 'name' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['update:sort']);

const { t } = useI18n();

const SORT_FIELDS = [
  ['name', 'NAME'],
  ['domain', 'DOMAIN'],
  ['created_at', 'CREATED_AT'],
];

const menuSections = computed(() => [
  {
    title: t('COMPANIES.SORT_BY.LABEL'),
    items: SORT_FIELDS.map(([value, key]) => ({
      label: t(`COMPANIES.SORT_BY.OPTIONS.${key}`),
      value,
      action: 'sort',
      icon: props.activeSort === value ? 'i-lucide-check' : 'i-lucide-minus',
      isSelected: props.activeSort === value,
    })),
  },
  {
    title: t('COMPANIES.ORDER.LABEL'),
    items: [
      {
        label: t('COMPANIES.ORDER.OPTIONS.ASCENDING'),
        value: 'asc',
        action: 'order',
        icon: 'i-lucide-arrow-up-narrow-wide',
        isSelected: props.activeOrdering === '',
      },
      {
        label: t('COMPANIES.ORDER.OPTIONS.DESCENDING'),
        value: 'desc',
        action: 'order',
        icon: 'i-lucide-arrow-down-wide-narrow',
        isSelected: props.activeOrdering === '-',
      },
    ],
  },
]);

const isMenuOpen = ref(false);

const handleAction = ({ action, value }) => {
  if (action === 'sort') {
    emit('update:sort', { sort: value, order: props.activeOrdering });
  } else if (action === 'order') {
    emit('update:sort', {
      sort: props.activeSort,
      order: value === 'desc' ? '-' : '',
    });
  }
};
</script>

<template>
  <div v-on-clickaway="() => (isMenuOpen = false)" class="relative">
    <Button
      v-tooltip.bottom="t('COMPANIES.MORE_ACTIONS')"
      icon="i-lucide-ellipsis-vertical"
      color="slate"
      variant="ghost"
      size="sm"
      :class="isMenuOpen ? 'bg-n-alpha-2' : ''"
      @click="isMenuOpen = !isMenuOpen"
    />
    <DropdownMenu
      v-if="isMenuOpen"
      :menu-sections="menuSections"
      class="ltr:right-0 rtl:left-0 mt-1 w-56 top-full"
      @action="handleAction"
    />
  </div>
</template>
